import React, { useEffect, useRef } from 'react';
import { Card, CardContent, Typography, CircularProgress } from '@mui/material';
import { Chart } from 'chart.js/auto';
import { useHistoricalData } from '../hooks/useHistoricalData';
import { getTimeRange } from '../utils/utils';
import { startOfHour, addHours, isAfter, subHours } from 'date-fns';
import { toZonedTime, format } from 'date-fns-tz';
import 'chartjs-adapter-date-fns';
import annotationPlugin from 'chartjs-plugin-annotation';
Chart.register(annotationPlugin);

const highlightPlugin = {
  id: 'highlightPlugin',
  afterTooltipDraw: (chart) => {
    const tooltip = chart.tooltip;
    const datasets = chart.data.datasets;

    datasets.forEach((dataset) => {
      if (dataset._originalPointRadius && dataset._originalPointBackgroundColor && dataset._originalPointBorderWidth && dataset._originalPointBorderColor) {
        dataset.pointRadius = [...dataset._originalPointRadius];
        dataset.pointBackgroundColor = [...dataset._originalPointBackgroundColor];
        dataset.pointBorderWidth = [...dataset._originalPointBorderWidth];
        dataset.pointBorderColor = [...dataset._originalPointBorderColor];
      }
    });

    if (tooltip && tooltip.getActiveElements().length > 0) {
      const activeElements = tooltip.getActiveElements().slice(0, 2);
      activeElements.forEach((activeElement) => {
        const dataset = datasets[activeElement.datasetIndex];
        const index = activeElement.index;

        if (!dataset._originalPointRadius) {
          dataset._originalPointRadius = [...dataset.pointRadius];
          dataset._originalPointBackgroundColor = [...dataset.pointBackgroundColor];
          dataset._originalPointBorderWidth = [...dataset.pointBorderWidth];
          dataset._originalPointBorderColor = [...(dataset.pointBorderColor || Array(dataset.data.length).fill('transparent'))];
        }

        dataset.pointRadius[index] = 4;
        dataset.pointBackgroundColor[index] = '#000000';
        dataset.pointBorderWidth[index] = 1;
        dataset.pointBorderColor[index] = dataset.label === 'PM10 (μg/m³)' ? 'rgba(211, 84, 0, 0.8)' : 'rgba(243, 156, 18, 0.8)';
      });
    }
  },
};

const ParticulateMatterHistory = ({ site, dateRange, timezone, refreshInterval }) => {
  const { combinedData, loading, error } = useHistoricalData(site, dateRange, refreshInterval);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const lastX = useRef(null);
  const lastSelectedIndex = useRef(null);

  const { start, end } = getTimeRange(dateRange);
  const hourlyData = [];
  let currentHour = startOfHour(new Date(start));
  const endTime = new Date(end);

  while (!isAfter(currentHour, endTime)) {
    const timeUtc = currentHour.toISOString();
    const dataPoint = combinedData.find((d) => {
      const dataHour = startOfHour(new Date(d.time_utc)).getTime();
      return dataHour === currentHour.getTime() && (d.pm2_5_ugm3 != null || d.pm10_ugm3 != null);
    });
    hourlyData.push({
      time_utc: timeUtc,
      pm2_5_ugm3: dataPoint ? dataPoint.pm2_5_ugm3 : null,
      pm10_ugm3: dataPoint ? dataPoint.pm10_ugm3 : null,
    });
    currentHour = addHours(currentHour, 1);
  }

  const labels = hourlyData
    .map((data) => {
      const date = new Date(data.time_utc);
      return isNaN(date.getTime()) ? null : toZonedTime(date, timezone);
    })
    .filter((date) => date !== null);
  const pm25Data = hourlyData.map((data) => data.pm2_5_ugm3);
  const pm10Data = hourlyData.map((data) => data.pm10_ugm3);

  const minTime = Math.min(...labels.map((d) => d.getTime()));
  const maxTime = Math.max(...labels.map((d) => d.getTime()));

  const initialAnnotations = labels.reduce((acc, label, index) => {
    const startTime = subHours(label, 1).getTime();
    if (pm10Data[index] !== null) {
      acc[`line-pm10-${index}`] = {
        type: 'line',
        xMin: startTime,
        xMax: label.getTime(),
        yMin: pm10Data[index],
        yMax: pm10Data[index],
        borderColor: 'rgba(211, 84, 0, 0.7)',
        borderWidth: 2,
        display: true,
      };
    }
    if (pm25Data[index] !== null) {
      acc[`line-pm25-${index}`] = {
        type: 'line',
        xMin: startTime,
        xMax: label.getTime(),
        yMin: pm25Data[index],
        yMax: pm25Data[index],
        borderColor: 'rgba(243, 156, 18, 0.7)',
        borderWidth: 2,
        display: true,
      };
    }
    return acc;
  }, {});


  const cursorLinePlugin = {
    id: 'cursorLine',
    afterEvent: (chart, args) => {
      const { event } = args;
      if (event.type === 'mousemove') {
        const x = chart.scales.x.getValueForPixel(event.x);
        if (x !== lastX.current) {
          lastX.current = x;
          chart.options.plugins.annotation.annotations.cursorLine.xMin = x;
          chart.options.plugins.annotation.annotations.cursorLine.xMax = x;
          chart.update('none');
        }
      }
    },
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'PM10 (μg/m³)',
        data: pm10Data,
        borderColor: 'rgba(211, 84, 0, 0.8)',
        borderWidth: 0,
        fill: false,
        tension: 0,
        pointRadius: Array(pm10Data.length).fill(3),
        pointBackgroundColor: Array(pm10Data.length).fill('rgba(211, 84, 0, 0.8)'),
        pointBorderWidth: Array(pm10Data.length).fill(0),
        pointBorderColor: Array(pm10Data.length).fill('rgba(211, 84, 0, 0.8)'),
        pointHitRadius: 10,
        hidden: false,
      },
      {
        label: 'PM2.5 (μg/m³)',
        data: pm25Data,
        borderColor: 'rgba(243, 156, 18, 0.8)',
        borderWidth: 0,
        fill: false,
        tension: 0,
        pointRadius: Array(pm25Data.length).fill(3),
        pointBackgroundColor: Array(pm25Data.length).fill('rgba(243, 156, 18, 0.8)'),
        pointBorderWidth: Array(pm25Data.length).fill(0),
        pointBorderColor: Array(pm25Data.length).fill('rgba(243, 156, 18, 0.8)'),
        pointHitRadius: 10,
        hidden: false,
      },
    ],
  };

  const chartOptions = {
    animation: {
      duration: 200,
      easing: 'linear',
    },
    plugins: {
      datalabels: { display: false },
      legend: {
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const index = legendItem.datasetIndex;
          const ci = chart.data.datasets[index];
          if (!ci) return;

          const currentVisibility = ci.hidden || false;
          ci.hidden = !currentVisibility;

          ci.pointBorderWidth = Array(ci.data.length).fill(currentVisibility ? 0 : 1);
          const annotationKeys = Object.keys(chart.options.plugins.annotation.annotations).filter((key) =>
            key.startsWith(`line-${ci.label.toLowerCase().replace(' ', '-')}-`)
          );
          annotationKeys.forEach((key) => {
            chart.options.plugins.annotation.annotations[key].display = !currentVisibility;
          });

          chart.update();
        },
        labels: {
          generateLabels: (chart) => {
            return chart.data.datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.borderColor,
              strokeStyle: dataset.borderColor,
              lineWidth: 1,
              hidden: dataset.hidden || false,
              index: i,
            }));
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        caretPadding: 15,
        caretSize: 0,
        position: 'nearest',
        animation: {
          duration: 0,
        },
        filter: (tooltipItem) => {
          const x = chartInstance.current?.options.plugins.annotation.annotations.cursorLine.xMin;
          if (!x) return false;
          const labelTime = labels[tooltipItem.dataIndex].getTime();
          const threshold = 3600000 / 2;
          const isInRange = Math.abs(labelTime - x) <= threshold;

          if (isInRange) {
            lastSelectedIndex.current = tooltipItem.dataIndex;
          }
          return tooltipItem.dataIndex === lastSelectedIndex.current || isInRange;
        },
        external: (context) => {
          const tooltipModel = context.tooltip;
          if (!tooltipModel || !tooltipModel.chart) return;
          const chart = tooltipModel.chart;
          const chartArea = chart.chartArea;
          const cursorX = chart.scales.x.getPixelForValue(lastX.current);

          const leftQuarterThreshold = chartArea.left + chartArea.width * 0.25;
          tooltipModel.xAlign = cursorX < leftQuarterThreshold ? 'right' : 'left';
        },
        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems || tooltipItems.length === 0) return '';
            const time = new Date(tooltipItems[0].parsed.x);
            return format(time, 'MM/dd HH:mm', { timeZone: timezone });
          },
          label: (context) => `${context.dataset.label}: ${context.parsed.y} μg/m³`,
          labelColor: (context) => {
            if (context.dataset.label === 'PM10 (μg/m³)') {
              return {
                backgroundColor: 'rgba(211, 84, 0, 0.8)',
                borderColor: 'rgba(211, 84, 0, 0.8)',
              };
            }
            return {
              backgroundColor: 'rgba(243, 156, 18, 0.8)',
              borderColor: 'rgba(243, 156, 18, 0.8)',
            };
          },
        },
      },
      annotation: {
        drawTime: 'afterDatasetsDraw',
        annotations: {
          cursorLine: {
            type: 'line',
            xMin: null,
            xMax: null,
            borderColor: 'rgba(0, 0, 0, 0.5)',
            borderWidth: 1,
            borderDash: [5, 5],
          },
          ...initialAnnotations,
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          stepSize: 1,
          displayFormats: { hour: 'MM/dd HH:mm' },
        },
        min: minTime,
        max: maxTime,
        title: { display: true, text: 'Time' },
      },
      y: {
        title: { display: true, text: 'Concentration (μg/m³)' },
        beginAtZero: true,
      },
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) chartInstance.current.destroy();
      chartInstance.current = new Chart(chartRef.current, {
        type: 'line',
        data: chartData,
        options: chartOptions,
        plugins: [cursorLinePlugin, highlightPlugin],
      });
    }
    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [hourlyData, timezone]);

  if (loading)
    return (
      <Card sx={{ height: { xs: 520, md: 460 } }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <CircularProgress sx={{ color: 'var(--color-brand)' }} />
        </CardContent>
      </Card>
    );
  if (error)
    return (
      <Card sx={{ height: { xs: 520, md: 460 } }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="error">Error: {error}</Typography>
        </CardContent>
      </Card>
    );
  if (!hourlyData || hourlyData.length === 0)
    return (
      <Card sx={{ height: { xs: 520, md: 460 } }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography>No particulate matter data available</Typography>
        </CardContent>
      </Card>
    );

  return (
    <Card sx={{ height: { xs: 520, md: 460 }, overflow: 'hidden' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.2 }}>
        <Typography variant="h6" sx={{ color: 'var(--color-brand-dark)', mb: 0.25 }}>
          Particulate Matter History
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--color-muted)', mb: 1 }}>
          PM2.5 and PM10 concentration trends
        </Typography>
        <canvas ref={chartRef} style={{ flex: 1, maxHeight: '348px' }} />
      </CardContent>
    </Card>
  );
};

export default ParticulateMatterHistory;

